import { useDateTimeDisplay } from '../../../../../platform/util/useDateTimeDisplay';
import CalendarDateTimeField from '../../../../../platform/util/CalendarDateTimeField';
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import UpgradePlan from "../../../../../platform/util/upgrade/upgradePlan";
import {
  FeatureAccess,
  getFeatureAccess,
} from "../../../../../platform/util/featureAccess";



const calcDuration = (startIso, endIso) => {
  const start = new Date(startIso);
  const end = endIso ? new Date(endIso) : new Date();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "-";
  const ms = Math.max(0, end.getTime() - start.getTime());
  const totalMinutes = Math.floor(ms / (1000 * 60));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
};

const calcDurationMinutes = (startIso, endIso) => {
  const start = new Date(startIso);
  const end = endIso ? new Date(endIso) : new Date();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  const ms = Math.max(0, end.getTime() - start.getTime());
  return Math.floor(ms / (1000 * 60));
};

const minutesToLabel = (minutes) => {
  const total = Math.max(0, Number(minutes || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${m}m`;
};

const parseRecords = (raw) => {
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item?.id && item?.clockInAt);
  } catch (_err) {
    return [];
  }
};

const toDateTimeLocalInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const two = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(
    d.getHours()
  )}:${two(d.getMinutes())}`;
};

const toIsoFromLocalInput = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export default function StaffAttendanceTab({ staff, isDisabled = false }) {
  const { displayCalendar, displayClock, dateLabel: toLocalDate, clockLabel } = useDateTimeDisplay();
  const toLocalTime = value => clockLabel(new Date(value).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }));
  const [inValid, setInValid] = useState(true);
  const [outValid, setOutValid] = useState(true);
  const { providerUser } = useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const businessId = selectedBusiness?.businessBasic?.id;
  const staffId = staff?.id;

  const attendanceAccess = getFeatureAccess(
    providerUser,
    FeatureAccess.STAFF_ATTENDANCE
  );
  const premiumPlanRequired = attendanceAccess.requiredPlanId;
  const hasPremiumAttendance = attendanceAccess.hasAccess;
  const controlsDisabled = Boolean(isDisabled || !hasPremiumAttendance);

  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
  const [records, setRecords] = useState([]);
  const [clockInNote, setClockInNote] = useState("");
  const [tick, setTick] = useState(0);

  const [editRecord, setEditRecord] = useState(null);
  const [editClockIn, setEditClockIn] = useState("");
  const [editClockOut, setEditClockOut] = useState("");
  const [editNote, setEditNote] = useState("");

  const storageKey = useMemo(
    () => `staffAttendance:${businessId || "unknown"}:${staffId || "unknown"}`,
    [businessId, staffId]
  );

  useEffect(() => {
    if (!storageKey) return;
    const raw = sessionStorage.getItem(storageKey);
    setRecords(parseRecords(raw));
  }, [storageKey]);

  const persist = (nextRecords) => {
    setRecords(nextRecords);
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(nextRecords));
    } catch (_err) {
      // non-blocking session persistence
    }
  };

  useEffect(() => {
    const id = setInterval(() => setTick((value) => value + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const activeRecord = useMemo(
    () => records.find((row) => !row.clockOutAt) || null,
    [records]
  );

  const todayMinutes = useMemo(() => {
    const now = new Date(Date.now() + tick * 0);
    return records
      .filter((row) => {
        const d = new Date(row.clockInAt);
        return (
          !Number.isNaN(d.getTime()) &&
          d.getFullYear() === now.getFullYear() &&
          d.getMonth() === now.getMonth() &&
          d.getDate() === now.getDate()
        );
      })
      .reduce(
        (sum, row) => sum + calcDurationMinutes(row.clockInAt, row.clockOutAt),
        0
      );
  }, [records, tick]);

  const weeklyMinutes = useMemo(() => {
    const now = new Date(Date.now() + tick * 0);
    const day = now.getDay();
    const weekStart = new Date(now);
    weekStart.setHours(0, 0, 0, 0);
    weekStart.setDate(now.getDate() - day);

    return records
      .filter((row) => {
        const d = new Date(row.clockInAt);
        return !Number.isNaN(d.getTime()) && d >= weekStart;
      })
      .reduce(
        (sum, row) => sum + calcDurationMinutes(row.clockInAt, row.clockOutAt),
        0
      );
  }, [records, tick]);

  const handlePremiumBlockedAction = () => {
    if (!hasPremiumAttendance) {
      setCheckPlanNeeded(true);
      return true;
    }
    return false;
  };

  const handleClockIn = () => {
    if (controlsDisabled || activeRecord || handlePremiumBlockedAction()) return;

    const now = new Date().toISOString();
    const next = [
      {
        id: `${Date.now()}`,
        clockInAt: now,
        clockOutAt: null,
        note: clockInNote?.trim() || "",
      },
      ...records,
    ];

    persist(next);
    setClockInNote("");
  };

  const handleClockOut = () => {
    if (controlsDisabled || !activeRecord || handlePremiumBlockedAction()) return;

    const now = new Date().toISOString();
    const next = records.map((row) =>
      row.id === activeRecord.id ? { ...row, clockOutAt: now } : row
    );

    persist(next);
  };

  const handleOpenEdit = (record) => {
    if (!record || controlsDisabled || handlePremiumBlockedAction()) return;
    setEditRecord(record);
    setEditClockIn(toDateTimeLocalInput(record.clockInAt));
    setEditClockOut(toDateTimeLocalInput(record.clockOutAt));
    setEditNote(record.note || "");
  };

  const handleSaveEdit = () => {
    if (!editRecord) return;

    const nextClockIn = toIsoFromLocalInput(editClockIn);
    const nextClockOut = toIsoFromLocalInput(editClockOut);

    if (!nextClockIn) {
      window.alert("Clock in time is required.");
      return;
    }

    if (nextClockOut && new Date(nextClockOut) < new Date(nextClockIn)) {
      window.alert("Clock out cannot be earlier than clock in.");
      return;
    }

    const next = records.map((row) =>
      row.id === editRecord.id
        ? {
            ...row,
            clockInAt: nextClockIn,
            clockOutAt: nextClockOut,
            note: editNote?.trim() || "",
          }
        : row
    );

    persist(next);
    setEditRecord(null);
  };

  const handleDeleteRecord = (record) => {
    if (!record || controlsDisabled || handlePremiumBlockedAction()) return;

    const isActive = !record.clockOutAt;
    const ok = window.confirm(
      isActive
        ? "Delete the active shift? This cannot be undone."
        : "Delete this attendance entry?"
    );
    if (!ok) return;

    persist(records.filter((row) => row.id !== record.id));
  };

  const handleExportCsv = () => {
    if (!records.length) return;

    const header = "Date,Clock In,Clock Out,Duration,Note";
    const lines = records.map((row) => {
      const date = toLocalDate(row.clockInAt);
      const inTime = toLocalTime(row.clockInAt);
      const outTime = row.clockOutAt ? toLocalTime(row.clockOutAt) : "";
      const duration = calcDuration(row.clockInAt, row.clockOutAt);
      const note = String(row.note || "").replace(/"/g, '""');
      return `"${date}","${inTime}","${outTime}","${duration}","${note}"`;
    });

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${staff?.id || "staff"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {checkPlanNeeded && (
        <UpgradePlan
          featureName="Time tracking and attendance workflows"
          requiredPlanId={premiumPlanRequired}
          onClose={() => setCheckPlanNeeded(false)}
        />
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Time Tracking & Attendance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track shift start/end quickly for this staff member.
      </Typography>

      {!hasPremiumAttendance && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Premium plan required for time tracking and attendance workflows.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          sx={{ mb: 1.5 }}
        >
          <Button
            variant="contained"
            onClick={handleClockIn}
            disabled={controlsDisabled || Boolean(activeRecord)}
          >
            Clock In
          </Button>

          <Button
            variant="outlined"
            color="warning"
            onClick={handleClockOut}
            disabled={controlsDisabled || !activeRecord}
          >
            Clock Out
          </Button>

          <Button variant="text" onClick={handleExportCsv} disabled={!records.length}>
            Export CSV
          </Button>

          {activeRecord ? (
            <Chip
              color="success"
              label={`On shift - ${calcDuration(activeRecord.clockInAt, null)}`}
            />
          ) : (
            <Chip variant="outlined" label="Not on shift" />
          )}

          <Chip variant="outlined" label={`Today ${minutesToLabel(todayMinutes)}`} />
          <Chip variant="outlined" label={`This week ${minutesToLabel(weeklyMinutes)}`} />
        </Stack>

        <TextField
          label="Shift note (optional, no PHI)"
          size="small"
          fullWidth
          value={clockInNote}
          onChange={(e) => setClockInNote(e.target.value)}
          disabled={controlsDisabled || Boolean(activeRecord)}
          placeholder="Example: Day shift or handoff notes"
        />
      </Paper>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Clock In</TableCell>
              <TableCell>Clock Out</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Note</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No attendance records yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              records.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{toLocalDate(row.clockInAt)}</TableCell>
                  <TableCell>{toLocalTime(row.clockInAt)}</TableCell>
                  <TableCell>{row.clockOutAt ? toLocalTime(row.clockOutAt) : "-"}</TableCell>
                  <TableCell>{calcDuration(row.clockInAt, row.clockOutAt)}</TableCell>
                  <TableCell>{row.note || "-"}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleOpenEdit(row)}
                        disabled={controlsDisabled}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="text"
                        onClick={() => handleDeleteRecord(row)}
                        disabled={controlsDisabled}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={Boolean(editRecord)}
        onClose={() => setEditRecord(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Attendance Entry</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <CalendarDateTimeField label="Clock In" value={editClockIn} onChange={setEditClockIn} calendar={displayCalendar} clock={displayClock} timezone={Intl.DateTimeFormat().resolvedOptions().timeZone} onValidityChange={setInValid} />

            <CalendarDateTimeField label="Clock Out (optional)" value={editClockOut} onChange={setEditClockOut} calendar={displayCalendar} clock={displayClock} timezone={Intl.DateTimeFormat().resolvedOptions().timeZone} onValidityChange={setOutValid} />
            <Button onClick={() => setEditClockOut('')}>Keep shift active (clear clock out)</Button>

            <TextField
              label="Note"
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditRecord(null)}>Cancel</Button>
          <Button variant="contained" disabled={!inValid || (Boolean(editClockOut) && !outValid)} onClick={handleSaveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
